import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, ArrowUpRight } from 'lucide-react';
import logo from '../assets/images/logo.svg';
import Facebook from '../assets/images/Facebook.svg';
import Linkedin from '../assets/images/Linkedin.svg';
import Instagram from '../assets/images/Instagram.svg';
import site from '../content/site';
import services from '../content/services';

const socials = [
  { icon: Facebook, name: 'Facebook', href: site.social.facebook },
  { icon: Linkedin, name: 'LinkedIn', href: site.social.linkedin },
  { icon: Instagram, name: 'Instagram', href: site.social.instagram },
];

const company = [
  { name: 'About', to: '/about' },
  { name: 'Work', to: '/work' },
  { name: 'Our Process', to: '/process' },
  { name: 'Services', to: '/services' },
  { name: 'Contact', to: '/contact' },
];

const Footer = () => (
  <footer className="bg-primary text-white" id="footer">
    <div className="container-page pt-16 md:pt-20 pb-10">
      <div className="grid grid-cols-2 gap-x-8 gap-y-12 md:grid-cols-12">
        {/* Brand */}
        <div className="col-span-2 md:col-span-4">
          <Link to="/" aria-label="Codelithic Labs — home" className="inline-block">
            <img
              src={logo}
              alt="Codelithic Labs Inc."
              width="375"
              height="78"
              loading="lazy"
              decoding="async"
              className="h-9 w-auto brightness-0 invert"
            />
          </Link>
          <p className="mt-6 max-w-xs leading-relaxed text-white/60">
            A Calgary-based technology partner building software, cloud and security
            solutions for clients worldwide.
          </p>
          <div className="mt-7 flex gap-3">
            {socials.map((s) => (
              <a
                key={s.name}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                aria-label={`Codelithic Labs on ${s.name}`}
                className="rounded-full bg-white/10 p-2.5 transition-all hover:bg-secondary active:scale-90"
              >
                <img
                  src={s.icon}
                  alt=""
                  aria-hidden="true"
                  width="24"
                  height="24"
                  loading="lazy"
                  className="h-5 w-5 invert"
                />
              </a>
            ))}
          </div>
        </div>

        {/* Services — every service page linked from every page, which is how
            internal link equity reaches the deeper routes. */}
        <div className="col-span-2 sm:col-span-1 md:col-span-4">
          <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-white/40">Services</h2>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {services.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  className="-my-1 inline-block py-1.5 text-sm text-white/70 transition-colors hover:text-secondary"
                >
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Company */}
        <div className="md:col-span-2">
          <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-white/40">Company</h2>
          <ul className="mt-6 space-y-3">
            {company.map((c) => (
              <li key={c.to}>
                <Link to={c.to} className="-my-1 inline-block py-1.5 text-sm text-white/70 transition-colors hover:text-secondary">
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div className="md:col-span-2">
          <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-white/40">Contact</h2>
          <ul className="mt-6 space-y-4 text-sm">
            <li>
              <a
                href={`mailto:${site.email}`}
                className="group -my-1 inline-flex items-start gap-2.5 py-1.5 text-white/70 transition-colors hover:text-secondary"
              >
                <Mail size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
                <span className="break-all">{site.email}</span>
                <ArrowUpRight
                  size={13}
                  aria-hidden="true"
                  className="mt-0.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                />
              </a>
            </li>
            <li className="flex items-start gap-2.5 text-white/70">
              <MapPin size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
              <address className="not-italic">
                {site.address.street}
                <br />
                {site.address.locality}, {site.address.region}
                <br />
                {site.address.countryName}
              </address>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-sm text-white/40 sm:flex-row">
        <p>
          &copy; {new Date().getFullYear()} {site.legalName} All rights reserved.
        </p>
        <nav aria-label="Legal" className="flex gap-6">
          <Link to="/privacy-policy" className="py-1 transition-colors hover:text-white">
            Privacy Policy
          </Link>
          <Link to="/terms-of-service" className="py-1 transition-colors hover:text-white">
            Terms of Service
          </Link>
        </nav>
      </div>
    </div>
  </footer>
);

export default Footer;
